<template>
  <section class="box-layout">
    <div v-if="!isLoading" class="referral__content">
      <header class="content__header">
        <h2>{{ $t('userInfo.lnb.referrals') }}</h2>
        <div class="content__header-right">
          <span>{{ $t('referral.referrals.payback') }}</span>
          <a :href="learnMoreLink" target="_blank">
            <span>{{ $t('referral.referrals.learnMore') }}</span>
            <i class="icon icon--tooltip-link size-12"></i>
          </a>
        </div>
      </header>
      <div class="content__body" ref="scrollContent">
        <template v-if="referralCode">
          <div class="content__top">
            <content-box :width="'18%'" :height="'252px'">
              <p class="content__title">
                {{ $t('common.referralCode') }}
              </p>
              <div class="content referral-code">
                <div class="code">{{ referralCode }}
                  <p ref="copied-alert" class="copied">{{ $t('referral.referrals.copied') }}</p>
                </div>
                <ui-button :iconName="'icon--copy-dark size-13'" @click="copyReferral">
                  {{ $t('common.copy') }}
                </ui-button>
              </div>
            </content-box>
            <content-box :width="'45%'" :height="'252px'">
              <p class="content__title">{{ $t('referral.referrals.status.title') }}</p>
              <div v-if="totalReferCount" class="content referral-status">
                <div class="pie-chart" :style="{ background: referralStatusConicGradient }">
                  <span class="pie-chart-center"></span>
                  <p class="pie-chart-label">
                    <span>{{ totalReferCount | thousand }}</span>
                    <span class="label-sub">({{ $t('referral.referrals.status.all') }})</span>
                  </p>
                </div>
                <div class="status-list">
                  <div :class="`status-item ${key}`" v-for="(value, key) in status" :key="key">
                    <p class="status-label">{{ $t(`referral.referrals.status.${key}`) }}</p>
                    <hr>
                    <p class="status-value">{{ value | thousand }}</p>
                  </div>
                </div>
              </div>
              <p v-else class="empty">{{ $t('referral.referrals.status.empty') }}</p>
            </content-box>
            <content-box :width="'37%'" :height="'252px'">
              <p class="content__title">
                <span>{{ $t('referral.referrals.reward.title') }}</span>
                <span v-if="totalRewardCount" class="content__title-sub">
                  {{ $t('referral.referrals.reward.accumulated', { count: totalRewardCount.toLocaleString() }) }}
                  ({{ $t('referral.referrals.reward.worth', { worth: rewordWorth.toLocaleString() }) }})
                </span>
              </p>
              <div v-if="totalRewardCount" class="content referral-reward">
                <div class="bar-chart" :style="{ background: rewardBarBackground }"></div>
                <div class="status-list">
                  <div :class="`status-item ${key}`" v-for="(value, key) in reward" :key="key">
                    <p class="status-label">
                      {{ $t(`referral.referrals.reward.${key}`) }}
                      <a v-if="key === 'used'" :href="`${BA_URL}/user/jam`" target="_blank">
                        <i class="icon icon--link-right size-12"></i>
                      </a>
                    </p>
                    <hr>
                    <p class="status-value">
                      {{ value | thousand }}
                      <span class="status-value-sub">JAM</span>
                    </p>
                  </div>
                </div>
              </div>
              <p v-else class="empty" v-html="$t('referral.referrals.reward.empty')"></p>
            </content-box>
          </div>
          <div class="content__middle">
            <div class="chart-type">
              <button :class="{ on: this.currentChart === 'referrals' }"
                      @click="onSelectChartType('referrals')">
                {{ $t('referral.referrals.history.referredHistory') }}
              </button>
              <button :class="{ on: this.currentChart === 'rewards' }"
                      @click="onSelectChartType('rewards')">
                {{ $t('referral.referrals.history.rewardsHistory') }}
              </button>
            </div>
            <referrals-chart v-if="this.currentChart === 'referrals'" :referralCode="referralCode" />
            <rewards-chart v-if="this.currentChart === 'rewards'" />
          </div>
          <back-to-top refName="scrollContent"/>
        </template>
        <template v-else>
          <content-box class="no-referral" :width="'800px'" :height="'450px'">
            <div class="text-area">
              <div>
                <i class="icon icon--logo-new"></i>
                <h2 class="title">{{ $t('userInfo.lnb.referrals') }}</h2>
              </div>
              <p class="payback" v-html="$t('referral.referrals.payback')"></p>
              <p class="description" v-html="$t('referral.referrals.noReferral.description')"></p>
            </div>
            <i class="icon icon--referral-bg"></i>
            <ui-button :primary="true"
                       :size="'large'"
                       :iconName="'icon--plus-white'"
                       @click="goToReferral">
              {{ $t('referral.referrals.noReferral.create') }}
            </ui-button>
          </content-box>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import { BA_URL } from '@/libs/utils'
import { EventBus } from '@/libs/event-bus'
import { getReferralInfo } from '@/api/referral'

import ContentBox from '@/components/ui/ContentBox'
import BackToTop from '@/components/ui/BackToTop'
import ReferralsChart from '@/components/user-info/manage-plan/ReferralsChart'
import RewardsChart from '@/components/user-info/manage-plan/RewardsChart'
import UiButton from '@/components/ui/UiButton'

const COLOR_LIST = {
  paid: '#EC0047',
  free: '#4ECBE1',
  deactivated: '#E0E0E0',
  available: '#F46200',
  pending: '#720022',
  used: '#D7D7D7'
}

export default {
  name: 'Referrals',
  components: {
    ContentBox,
    BackToTop,
    UiButton,
    ReferralsChart,
    RewardsChart
  },
  data () {
    return {
      isLoading: true,
      currentChart: 'referrals',
      referralCode: '',
      reward: {
        available: 0,
        pending: 0,
        used: 0
      },
      status: {
        paid: 0,
        free: 0,
        deactivated: 0
      },
      BA_URL: BA_URL
    }
  },
  async created () {
    await getReferralInfo().then(res => {
      this.referralCode = res.referral
      if (res.status) {
        this.status = res.status
      }
      if (res.reward) {
        this.reward = res.reward
      }
    }).catch(e => {
      console.log(e)
    })
    EventBus.$emit('onLoading', false)
    this.isLoading = false
  },
  computed: {
    totalReferCount () {
      return Object.values(this.status).reduce((sum, item) => sum + item, 0)
    },
    totalRewardCount () {
      return Object.values(this.reward).reduce((sum, item) => sum + item, 0)
    },
    rewordWorth () {
      if (this.$i18n.locale === 'en') {
        return this.totalRewardCount
      } else if (this.$i18n.locale === 'ja') {
        return this.totalRewardCount * 100
      } else {
        return this.totalRewardCount * 1000
      }
    },
    referralStatusConicGradient () {
      const segments = Object.values(this.status)
      const total = segments.reduce((sum, item) => sum + item, 0)

      let currentPercent = 0
      const gradientParts = Object.entries(this.status).map(([key, value]) => {
        const start = currentPercent
        const end = currentPercent + (value / total) * 100
        currentPercent = end
        return `${COLOR_LIST[key]} ${start}% ${end}%`
      })
      return `conic-gradient(${gradientParts.join(', ')})`
    },
    rewardBarBackground () {
      const segments = Object.values(this.reward)
      const total = segments.reduce((sum, item) => sum + item, 0)

      let current = 0
      const gradientParts = Object.entries(this.reward).map(([key, value]) => {
        const start = (current / total) * 100
        current += value
        const end = (current / total) * 100
        return `${COLOR_LIST[key]} ${start.toFixed(2)}% ${end.toFixed(2)}%`
      })

      return `linear-gradient(to top, ${gradientParts.join(', ')})`
    },
    learnMoreLink () {
      switch (this.$i18n.locale) {
        case 'en':
          return 'https://forum.beusable.net/en/solomon/post/2467?utm_source=Referrals_en&utm_medium=page&utm_campaign=referrals'
        case 'ja':
          return 'https://forum.beusable.net/ja/solomon/post/2469?utm_source=Referrals_ja&utm_medium=page&utm_campaign=referrals'
        default:
          return 'https://forum.beusable.net/ko/solomon/post/2454?utm_source=Referrals&utm_medium=page&utm_campaign=referrals'
      }
    }
  },
  methods: {
    onSelectChartType (value) {
      this.currentChart = value
    },
    copyReferral () {
      navigator.clipboard.writeText(this.referralCode).then(() => {
        this.$refs['copied-alert'].style.display = 'flex'
        setTimeout(() => {
          this.$refs['copied-alert'].style.display = 'none'
        }, 2000)
      })
    },
    goToReferral () {
      this.$router.push({ name: 'myInformation', query: { fromReferral: true } })
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
  .box-layout {
    position: relative;
    padding-top: 33px;
    height: 100%;
    box-sizing: border-box;
    float: left;
    width: calc(100% - 211px);

  .referral__content {
    height: 100%;
    .content__header {
      display: flex;
      align-items: flex-end;
      gap: 12px;
      margin-left: 30px;
      margin-right: 30px;
      padding-bottom: 16px;

      h2 {
        font-size: 26px;
        font-weight: 500;
        color: #444444;
        padding-left: 10px;

        span {
          margin-left: 20px;
          font-size: 13px;
          font-weight: 500;
          font-stretch: normal;
          font-style: normal;
          line-height: 1;
          letter-spacing: normal;
          color: #767676;
        }
      }

      &-right {
        @include flexYcenter;
        gap: 10px;
        margin-bottom: 4px;
        color: #666;
        font-size: 12px;
        font-weight: 400;

        a {
          @include flexYcenter;
          gap: 4px;
          color: #0074FF;
          font-weight: 600;
          i {
            transform: rotate(90deg);
          }
        }
      }
    }
    .content__body {
      height: calc(100% - 55px);
      padding: 1px 30px 0;
      overflow-y: auto;

      .content--wrap {
        display: flex;
        flex-direction: column;
        position: relative;
        padding: 24px;
        box-sizing: border-box;

        .content__title {
          @include flexXbetween;
          font-size: 15px;
          font-weight: 600;
          color: #2f2f2f;
          &-sub {
            padding-top: 2px;
            color: #666;
            font-size: 12px;
            font-weight: 400;
          }
        }

        .content {
          @include flexYcenter;
          .status-list {
            display: flex;
            flex-direction: column;
            gap: 20px;
            flex: 1;
            padding: 8px 0;
            .status-item {
              @include flexYcenter;
              gap: 4px;
              .status-label {
                @include flexYcenter;
                gap: 4px;
                color: #444;
              }
              hr {
                flex: 1;
                border: none;
                border-top: 1px dashed #BBBBBB;
              }
              .status-value {
                @include flexYcenter;
                gap: 4px;
                font-size: 16px;
                font-weight: 600;
                .status-value-sub {
                  font-size: 14px;
                }
              }
              &:before {
                content: '';
                display: block;
                width: 12px;
                height: 12px;
                border-radius: 50%;
                margin-right: 4px;
              }
              &.paid {
                &:before {
                  background: $brandColor;
                }
                .status-value {
                  color: $brandColor;
                  font-size: 20px;
                }
              }
              &.free {
                &:before {
                  background: #16B5D2;
                }
                .status-value {
                  color: #13A5BF;
                }
              }
              &.deactivated {
                &:before {
                  background: #D7D7D7;
                }
                .status-value {
                  color: #AAA;
                }
              }
              &.available {
                &:before {
                  background: #F46200;
                }
                .status-value {
                  color: #F46200;
                }
              }
              &.pending {
                &:before {
                  background: #720022;
                }
                .status-value {
                  color: #8D002A;
                }
              }
              &.used {
                &:before {
                  background: #D7D7D7;
                }
                .status-value {
                  color: #AAA;
                }
              }
            }
          }

          &.referral-code {
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 12px 0;
            margin-top: 44px;

            /deep/ .ui-btn {
              font-weight: 500;
            }

            .code {
              position: relative;
              padding: 7px;
              border-radius: 4px;
              border: 1px solid #EBEBEB;
              background: #F4F4F4;
              color: #57AB00;
              font-size: 15px;
              font-weight: 600;

              .copied {
                display: none;
                align-items: center;
                position: absolute;
                top: 23px;
                left: 50%;
                transform: translateX(-50%);
                height: 32px;
                padding: 6px 16px;
                border-radius: 6px;
                background-color: #000;
                box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16);
                color: #68BB0C;
                font-size: 13px;
                font-weight: 400;
                line-height: 120%;
                white-space: nowrap;
                z-index: 2;
                box-sizing: border-box;
              }
            }

          }

          &.referral-status {
            @include flexXYcenter;
            margin-top: 29px;
            gap: 36px;
            padding: 0 12px;
            .pie-chart {
              display: inline-block;
              position: relative;
              width: 120px;
              height: 120px;
              border-radius: 50%;
              background: #F4F4F4;
              &-center {
                position: absolute;
                width: 43px;
                height: 43px;
                background: #fff;
                border-radius: 50%;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
              }
              &-label {
                @include flexXYcenter;
                gap: 4px;
                position: absolute;
                bottom: -9px;
                left: 50%;
                transform: translate(-50%, 100%);
                width: 100%;
                color: #666;
                font-weight: 600;
                font-size: 14px;
                .label-sub {
                  font-size: 12px;
                }
              }
            }
          }

          &.referral-reward {
            @include flexXYcenter;
            margin-top: 36px;
            gap: 42px;
            padding: 0 12px 0 26px;
            .bar-chart {
              width: 24px;
              height: 104px;
              border-radius: 50px;
              background: #F4F4F4;
            }
          }
        }

        .empty {
          width: 100%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          margin-bottom: 1px;
          color: #666;
          font-size: 12px;
          font-weight: 500;
          line-height: 120%; /* 14.4px */
          text-align: center;
        }
      }

      .content__top {
        @include flexXbetween;
        gap: 20px;
      }

      .content__middle {
        .chart-type {
          margin-top: 32px;
          margin-bottom: -17px;

          button {
            padding: 7px 3px;
            font-weight: 500;
            font-size: 15px;
            margin: 0 15px;
            color: #999999;

            &.on {
              color: #2f2f2f;
              border-bottom: 3px solid #ec0147;
            }

            &:first-child {
              margin-left: 10px;
            }
          }
        }
      }

      .no-referral {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: row;
        gap: 55px;
        padding: 110px 35px;
        border-radius: 34px;

        .text-area {
          display: flex;
          flex-direction: column;
          gap: 12px;

          .title {
            color: #555;
            font-size: 48px;
            font-weight: 600;
            margin-left: -2px;
          }

          .payback {
            color: #1199AF;
            font-size: 20px;
            font-weight: 600;
            line-height: 150%;
            max-width: 310px;
            word-break: keep-all;
          }

          .description {
            color: #444;
            font-size: 14px;
            line-height: 150%;
            max-width: 310px;
          }
        }

        .icon--referral-bg {
          margin-top: 18px;
        }

        .ui-btn {
          position: absolute;
          bottom: 34px;
          right: 34px;
        }
      }
    }
  }
}
</style>
